/** A Reciclagem: o que se apagou do servidor (revisões de modelos e
 *  sessões partilhadas) e ainda se pode restaurar. Fica 7 dias; depois o
 *  servidor apaga-o de vez (ver RETENCAO_RECICLAGEM no backend). Aqui só
 *  o que é puro: os tipos que a API devolve, a lista junta e os textos. */

/** Uma revisão na Reciclagem (`GET /api/modules/modelos/reciclagem`). */
export interface ModeloNaReciclagem {
  id: string
  nome: string
  revisao: number
  tamanho_comprimido: number
  apagado_em: string
  apagado_por_nome: string | null
  apagado_por_email: string | null
  /** Quando o servidor o apaga de vez. */
  expira_em: string
}

/** Uma sessão partilhada na Reciclagem (`GET /api/sessoes/reciclagem`). */
export interface SessaoNaReciclagem {
  id: string
  nome: string | null
  apagada_em: string
  expira_em: string
  bytes: number
}

/** Uma linha da Reciclagem, como a barra lateral a mostra. */
export interface ItemDaReciclagem {
  tipo: 'modelo' | 'sessao'
  id: string
  /** O nome, e numa revisão o número dela. */
  titulo: string
  expiraEm: string
}

/** "DD/MM" no fuso de quem vê. Vazio se a data não servir. */
export function dataCurta(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  return `${dd}/${mm}`
}

/** "apagado de vez a 23/10" (uma revisão) ou "apagada de vez a 23/10"
 *  (uma sessão). */
export function textoApagadoDeVez(expiraEm: string, feminino = false): string {
  const data = dataCurta(expiraEm)
  const apagado = feminino ? 'apagada' : 'apagado'
  return data ? `${apagado} de vez a ${data}` : `${apagado} de vez dentro de dias`
}

/** Os dias que o que se apaga fica na Reciclagem (o mesmo prazo do
 *  servidor). Só para os textos: a data certa vem em `expira_em`. */
export const DIAS_NA_RECICLAGEM = 7

/** A pergunta antes de apagar uma revisão do servidor. */
export function textoDeApagarModelo(nome: string, revisao: number): string {
  return (
    `Apagar do servidor a revisão ${revisao} de "${nome}"?\n\n`
    + `Vai para a Reciclagem, onde pode ser restaurada durante ${DIAS_NA_RECICLAGEM} dias; `
    + 'depois é apagada de vez. Até lá ninguém a abre a partir do servidor. Quem já a tem '
    + 'aberta não é afectado. (Para deixar de partilhar sem apagar, use "Partilhar com..." '
    + 'e "Retirar a partilha".)'
  )
}

/** As revisões e as sessões numa só lista, o que sai primeiro à frente. */
export function itensDaReciclagem(
  modelos: readonly ModeloNaReciclagem[],
  sessoes: readonly SessaoNaReciclagem[],
): ItemDaReciclagem[] {
  const itens: ItemDaReciclagem[] = [
    ...modelos.map((m) => ({
      tipo: 'modelo' as const,
      id: m.id,
      titulo: `${m.nome} · revisão ${m.revisao}`,
      expiraEm: m.expira_em,
    })),
    ...sessoes.map((s) => ({
      tipo: 'sessao' as const,
      id: s.id,
      titulo: `Sessão ${s.nome ? `"${s.nome}"` : 'sem nome'}`,
      expiraEm: s.expira_em,
    })),
  ]
  return itens.sort((a, b) => Date.parse(a.expiraEm) - Date.parse(b.expiraEm))
}
